<script setup lang="ts">

import MapLocationIcon from "@/assets/widgetIcons/MapLocationIcon.vue";
import {onMounted, ref} from "vue";
import {AboutTable} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import DefaultApplicationInfo from "@/utils/common/DefaultApplicationInfo.ts";
import FacebookIcon from "@/assets/widgetIcons/FacebookIcon.vue";
import TwitterIcon from "@/assets/widgetIcons/TwitterIcon.vue";
import YoutubeIcon from "@/assets/widgetIcons/YoutubeIcon.vue";
import LinkedinIcon from "@/assets/widgetIcons/LinkedinIcon.vue";
import InstagramIcon from "@/assets/widgetIcons/InstagramIcon.vue";
import SnapchatIcon from "@/assets/widgetIcons/SnapchatIcon.vue";
import TiktokIcon from "@/assets/widgetIcons/TiktokIcon.vue";
import SupportDateAlerts from "@/components/settings/SupportDateAlerts.vue";

const latestRelease = ref("...");
const licenceDetails = ref("...");
const customerSupportEndDate = ref("...")
const ECPA_EG = {
  header: Translator.translateId("egypt"),
  address: "<p>۹۰ شارع السيد المرغني، کلیة البنات، مصر الجديدة، القاهرة</p><p>90 El-Sayed El-Merghany, Kolleyet El Banat, Heliopolis, Cairo</p>",
  phoneNumbers: ["0020226909962", "0020226909961", "00201000671821"],
  email: "info@ecpa-eg.com",
  socials: {
    facebook: "https://www.facebook.com/EgyptianCPA",
    twitter: "https://twitter.com/EgyptianCPA",
    linkedin: "https://www.linkedin.com/company/egyptian-cpa/",
    youtube: "https://www.youtube.com/user/EgyptianCPAs",
  }
};
const ECPA_UAE = {
  header: Translator.translateId("uae"),
  label: "Combine Lines L.L.C",
  address: "<p>٤ شارع العرشان الحصن الخالدية بناية بنك ابو ظبي الاول مكتب رقم 1104 – الإمارات العربیة المتحدة</p><p>4 Al Irshan st Al Hisn Khaldia Fab bank Building office no 1104 – Abu Dhabi – UAE</p> <a class='tw-underline tw-text-primary-light dark:tw-text-primary-dark' href='https://goo.gl/maps/eMyfpYzrF1kZqkky8' target='_blank'>9 th Floor, Damac XL Tower,Business Bay, Dubai – UAE</a>",
  phoneNumbers: ["00971501242584", "00971502242290"],
  email: "info@ecpa-eg.com"
};
const AUTO_PRACTICE_EG = {
  header: Translator.translateId("egypt"),
  label: "Auto Practice",
  websiteLink: "autopractise.com",
  address: "<p>11ش عبدالحليم النجار - متفرع من عباس العقاد - مدينة نصر - القاهرة</p><p>11 Abdelhalim El-Nagar-Nasr city-Cairo</p>",
  email: "info@autopractise.com",
  socials: {
    facebook: "https://www.facebook.com/autoperp",
    twitter: "https://x.com/AutoPERP",
    youtube: "https://www.youtube.com/@AutoPERP",
    linkedin: "https://www.linkedin.com/company/autoperp/",
    instagram: "https://www.instagram.com/autoperp",
    snapchat: "https://www.snapchat.com/add/AutoPERP",
    tiktok: "https://www.tiktok.com/@autoperp"
  }
};
const SOFT_VISION = {
  header: Translator.translateId("egypt"),
  label: "SoftVision Business Solutions –  LLC",
  address: "<p>45 شارع قمبيز – نادي الصيد – الدقي – الجيزة – مصر.</p><p>45 Kambez St., Nadi Al-Saeed, Dokki, Giza Governorate, Egypt.</p>",
  phoneNumbers: ["00233365113", "00201000442100"],
  email: "info@softvisiongroup.com",
  socials: {
    linkedin: "https://www.linkedin.com/company/soft-vision",
  }
};
const EGYPT = {
  header: Translator.translateId("egypt"),
  address: "<p>فيلا 7 شارع الحديد والصلب - من شارع الفلاح - ميدان لبنان - المهندسين</p><p>Villa 7 Elhadid W Elsolb street - From Elfalah Street - Lebanon Square - Mohandseen</p>",
  phoneNumbers: ["0233464647"],
  email: "info@namasoft.com",
  socials: {
    facebook: "https://www.facebook.com/namasoft",
    twitter: "https://www.twitter.com/namasoft",
    youtube: "https://www.youtube.com/c/namasoft",
  }
};
const KSA = {
  header: Translator.translateId("saudiArabia"),
  address: "<p>الطريق الدائري الشرقي، بين مخرج 13 و مخرج 14، حى الربوة، الرياض، 11655، المملكة العربية السعودية</p><p>Eastern Ring Branch Road, Between Exit 13 and Exit 14, Ar Rabwah, Riyadh, 11655, Saudi Arabia</p>",
  phoneNumbers: ["00966114922213", "00966539147179", "00966583670970"],
  email: "info@namasoft.com",
  socials: {
    facebook: "https://www.facebook.com/namasoftksa",
    twitter: "https://x.com/namasoftksa"
  }
};
const KUWAIT = {
  header: Translator.translateId("kuwait"),
  address: "<p>السالمية - شارع سالم المبارك - برج الشمالية - دور 20</p><p>Salmiya - Salem Mubarak Street - Northern Tower - floor 20</p>",
  phoneNumbers: ["0096550634517", "0096522070208"],
};

const aboutRows = ref<AboutTable[]>([
  {
    header: Translator.translateId("appVersionNumber"),
    label: DefaultApplicationInfo.appVersion()
  },
  {
    header: Translator.translateId("dbBuildDate"),
    label: DefaultApplicationInfo.getBuildDate()
  },
  {
    header: Translator.translateId("latestRelease"),
    label: latestRelease
  },
  {
    header: Translator.translateId("customerSupportEndDate"),
    label: customerSupportEndDate
  }, {
    header: Translator.translateId("licenceDetails"),
    label: licenceDetails
  },
  ...brandingDependentInfo(),
]);

function brandingDependentInfo(): AboutTable[] {
  let branding: 'nama' | 'exceed' | 'softvision' | 'autopractice' | 'capital' = (window as any).branding || 'nama';
  if (branding === "exceed") {
    return [KUWAIT, EGYPT, KSA];
  } else if (branding === 'capital') {
    return [ECPA_EG, ECPA_UAE]
  } else if (branding === 'autopractice') {
    return [AUTO_PRACTICE_EG];
  } else if (branding === 'softvision') {
    return [SOFT_VISION, EGYPT, KSA, KUWAIT];
  }
  return [EGYPT, KSA, KUWAIT];
}

const socialComponents = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  youtube: YoutubeIcon,
  linkedin: LinkedinIcon,
  instagram: InstagramIcon,
  snapchat: SnapchatIcon,
  tiktok: TiktokIcon
}

onMounted(async () => {
  await DefaultApplicationInfo.updateSupportDateAlerts();
  latestRelease.value = await DefaultApplicationInfo.getLatestRelease();
  licenceDetails.value = await DefaultApplicationInfo.getLicenceDetails();
  customerSupportEndDate.value = await DefaultApplicationInfo.getCustomerSupportEndDate()
});

</script>

<template>
  <q-table grid flat dense :rows="aboutRows" :rows-per-page-options="[0]" hide-pagination class="table-no-margin dark:tw-text-white">
    <template v-slot:item="props">
      <div class="col-12">
        <div class="tw-flex tw-flex-col desktop:tw-grid tw-grid-cols-8 desktop:tw-items-center">
          <div
            class="tw-col-span-2 desktop:tw-p-2 desktop:tw-border-r desktop:tw-border-black/20 desktop:dark:tw-border-white/35 tw-h-full tw-flex tw-items-center desktop:tw-justify-center">
            <h1 v-text="props.row.header"
                class="tw-text-sm tw-capitalize tw-leading-none dark:tw-text-white tw-font-bold  tw-p-2 tw-text-center"/>
          </div>
          <div class="tw-col-span-6 tw-pt-0 tw-pb-3 tw-px-3 desktop:tw-p-3 typography--semi-bold">
            <p v-if="props.row.label">
              <SupportDateAlerts v-if="props.row.header === Translator.translateId('customerSupportEndDate')" :alwaysShow="true"/>
              <span v-else v-text="props.row.label"/>
            </p>
            <address class="tw-space-y-2">
              <a v-if="props.row.websiteLink" :href="'https://'+props.row.websiteLink" target="_blank">
                      <span class="tw-underline tw-text-primary-light dark:!tw-text-primary-dark"
                            v-text="props.row.websiteLink"/>
              </a>
              <div v-if="props.row.address" class="tw-flex tw-gap-1 tw-items-center ">
                <MapLocationIcon class="tw-w-4 tw-shrink-0 black-icon "/>
                <div v-html="props.row.address" class="tw-whitespace-normal"/>
              </div>
              <p v-if="props.row.phoneNumbers">
                <template v-for="(phoneNumber,index) in props.row.phoneNumbers">
                  <a v-text="phoneNumber" :href="`tel:${phoneNumber}`"
                     class="tw-underline tw-text-primary-light dark:tw-text-primary-dark"/>
                  <span v-text="' - '" v-if="props.row.phoneNumbers.length-1 !==index"/>
                </template>
              </p>
              <a v-text="props.row.email" v-if="props.row.email" :href="`mailto:${props.row.email}`"
                 class="tw-text-primary-light dark:tw-text-primary-dark tw-underline"/>
              <div v-if="props.row.socials" class="tw-flex tw-gap-2 tw-items-center">
                <a v-for="social in Object.keys(props.row.socials)" :href="props.row.socials[social]"
                   target="_blank">
                  <component :is="socialComponents[social]" class="tw-p-1 tw-w-7 tw-h-7 tw-rounded-full"/>
                </a>
              </div>
            </address>
          </div>
        </div>
        <q-separator class="tw-bg-black/20 dark:tw-bg-namaBackground-deep-light/35"/>
      </div>
    </template>
  </q-table>
</template>

<style scoped>

</style>
